import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, Phone } from "lucide-react";
import { scrollToId } from "./smooth";
import { PHONE, PHONE_LABEL, WHATSAPP_URL } from "@/lib/api";
import { WhatsAppIcon } from "./WhatsApp";
import { useT } from "@/lib/i18n";

const EASE = [0.16, 1, 0.3, 1];
const CLS = ["", "stroke-text italic", "text-[var(--red)]"];
const TXT = {
  no: {
    lines: ["Nøkkelen", "til mer", "kraft."], title: "Nøkkelen til mer kraft.",
    h1: "Chiptuning, ECU-programmering og bilnøkler i Stavanger",
    p1: "Bilnøkler og startsperre, chiptuning, ECU- og TCU-programmering, kloning av styreenheter og bilelektronikk.",
    p2: "RB TUN AS gjør elektronikken i bilen din til vår jobb.",
    cta: "Be om tilbud", alt: "Bilnøkkel med fjernkontroll", tags: "Transponder · Fjernkontroll · Keyless",
  },
  en: {
    lines: ["The key", "to more", "power."], title: "The key to more power.",
    h1: "Chiptuning, ECU programming and car keys in Stavanger",
    p1: "Car keys and immobilisers, chiptuning, ECU and TCU programming, control unit cloning and car electronics.",
    p2: "At RB TUN AS, the electronics in your car are our job.",
    cta: "Request a quote", alt: "Car key with remote control", tags: "Transponder · Remote · Keyless",
  },
};

const MaskLine = ({ children, i, className }) => (
  <span className="block overflow-hidden pb-[0.06em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "105%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.35 + i * 0.14, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const TiltFrame = () => {
  const t = useT(TXT);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <motion.div onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}
      initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, delay: 0.7, ease: EASE }}
      style={{ perspective: 1000 }} className="relative w-full" data-testid="hero-key-frame">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="clip-notch relative aspect-[4/5] w-full overflow-hidden border border-white/10 bg-[var(--surface)]">
        <img src="/img/keys.webp" alt={t.alt} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,transparent_25%,rgba(8,9,11,0.85)_80%)]" />
        <div style={{ transform: "translateZ(60px)" }}
          className="absolute bottom-5 left-5 right-5 font-mono text-[11px] uppercase tracking-widest text-zinc-300">
          <span>{t.tags}</span>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const t = useT(TXT);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const spot = useMotionValue("50% 40%");
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    spot.set(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
  };
  const spotBg = useTransform(spot, (v) => `radial-gradient(600px circle at ${v}, rgba(255,42,42,0.16), transparent 60%)`);

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden pt-16">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0">
        <img src="/img/hero.webp" alt="" className="h-full w-full object-cover opacity-35 grayscale" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08090b]/40 via-[#08090b]/70 to-[#08090b]" />
      </motion.div>
      <motion.div style={{ background: spotBg }} className="pointer-events-none absolute inset-0" />
      <div className="hairline-grid pointer-events-none absolute inset-0 opacity-40 [mask-image:linear-gradient(to_bottom,black,transparent_85%)]" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 pb-20 pt-16 sm:px-8 lg:grid-cols-12 lg:pt-24">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8 flex flex-wrap items-center gap-3 font-mono text-xs font-normal uppercase tracking-[0.25em] text-zinc-300" data-testid="hero-h1">
            <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--red)]" aria-hidden="true" />
            {t.h1}
          </motion.h1>
          <div className="font-display text-[clamp(4rem,13vw,10.5rem)] font-black uppercase leading-[0.82] tracking-[-0.02em]"
            data-testid="hero-title" aria-label={t.title}>
            {t.lines.map((l, i) => <MaskLine key={l} i={i} className={CLS[i]}>{l}</MaskLine>)}
          </div>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg">
            {t.p1}
            {" "}{t.p2}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9, ease: EASE }}
            className="mt-10 flex flex-col gap-3 sm:flex-row">
            <button data-testid="hero-cta-quote" onClick={() => scrollToId("kontakt")}
              className="group flex items-center justify-center gap-3 bg-[var(--red)] px-7 py-4 font-mono text-sm font-bold uppercase tracking-widest text-white transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-10px_rgba(255,42,42,0.7)]">
              {t.cta}
              <ArrowDownRight size={18} className="transition-transform group-hover:rotate-[-45deg]" />
            </button>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp"
              className="flex items-center justify-center gap-3 border border-[#25D366]/60 px-7 py-4 font-mono text-sm font-bold uppercase tracking-widest text-zinc-100 transition-colors hover:border-[#25D366] hover:bg-[#25D366]/10">
              <WhatsAppIcon size={18} className="text-[#25D366]" /> WhatsApp
            </a>
            <a href={`tel:${PHONE}`} data-testid="hero-cta-phone"
              className="flex items-center justify-center gap-3 border border-white/15 px-7 py-4 font-mono text-sm font-bold uppercase tracking-widest text-zinc-100 transition-colors hover:border-white/50 hover:bg-white/5">
              <Phone size={16} /> {PHONE_LABEL}
            </a>
          </motion.div>
        </motion.div>
        <div className="mx-auto w-full max-w-sm lg:col-span-5 lg:max-w-none">
          <TiltFrame />
        </div>
      </div>
    </section>
  );
};
